// Dealer-stock favourites, inquiries, inspections, purchases and billing (the
// /dealer/* routes on the auction API). Unlike services/dealerStock.js, which reads PUBLIC listing data
// from the marketing backend, everything here is per-account and goes through
// the authenticated axios handler.
//
// Responses come back already unwrapped to the JSON body ({ message, data, ... }).

import axiosInterceptors from "./axiosHandler";
import { baseURL } from "./index";
import { fetchDealerStockPrices } from "./dealerStock";

// A conversation reply: JSON for text alone, as it always was; multipart when
// photos (or, from staff, PDFs) come with it. files: File[].
const replyPayload = (body, files) => {
  if (!files || !files.length) return { body };
  const form = new FormData();
  form.append("body", body || "");
  files.forEach((file) => form.append("attachments", file));
  return form;
};

const query = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") qs.append(key, String(value));
  });
  const str = qs.toString();
  return str ? `?${str}` : "";
};

// ---- Customer ----

export const getDealerFavoriteIds = () =>
  axiosInterceptors().get(baseURL + "/dealer/favorites/ids");

export const getDealerFavorites = () =>
  axiosInterceptors().get(baseURL + "/dealer/favorites");

// { vehicleId, vehicle? } - `vehicle` is an optional fallback snapshot used only
// if the API cannot reach the dealer backend itself.
export const toggleDealerFavorite = (payload) =>
  axiosInterceptors().post(baseURL + "/dealer/favorites", payload);

export const removeDealerFavorite = (vehicleId) =>
  axiosInterceptors().delete(baseURL + `/dealer/favorites/${encodeURIComponent(vehicleId)}`);

export const createDealerInquiry = (payload) =>
  axiosInterceptors().post(baseURL + "/dealer/inquiries", payload);

export const getMyDealerInquiries = () =>
  axiosInterceptors().get(baseURL + "/dealer/inquiries");

// Opening a thread also clears the customer-side unread counter server-side.
export const getMyDealerInquiry = (id) =>
  axiosInterceptors().get(baseURL + `/dealer/inquiries/${encodeURIComponent(id)}`);

export const replyToDealerInquiry = (id, body, files) =>
  axiosInterceptors().post(baseURL + `/dealer/inquiries/${encodeURIComponent(id)}/messages`, replyPayload(body, files));

// Each purchase carries `billing` ({ invoiced, paid, balance, label,
// openInvoiceId - the oldest unpaid invoice, openInvoiceSentAt }), `plan`
// (the ordered steps for this car), `stepInfo` ({ summary, next }) and
// `trackable` (a ship position is available).
export const getMyDealerPurchases = () =>
  axiosInterceptors().get(baseURL + "/dealer/purchases");

// { data: { imo, name, latitude, longitude, speed, course, destination, eta,
//   reportedAt, source, fetchedAt } | null,
//   voyage: { imo, name, destination, eta, lastReportAt } | null }
//   eta / reportedAt are ISO strings. fetchedAt is when this position was
//   fetched; reportedAt when the ship sent it (days ago once it is mid-ocean,
//   out of range of shore receivers). voyage is where the ship says it is going,
//   there even when there is no position.
export const getMyDealerPurchaseTracking = (id) =>
  axiosInterceptors().get(baseURL + `/dealer/purchases/${encodeURIComponent(id)}/tracking`);

// Pickup or delivery, from the arrival section: { method: 'pickup'|'delivery',
// address (delivery only), notes }. 409 once staff have scheduled the hand-off.
export const setMyDealerPurchaseHandoff = (id, data) =>
  axiosInterceptors().put(baseURL + `/dealer/purchases/${encodeURIComponent(id)}/handoff`, data);

export const getMyDealerInspections = () =>
  axiosInterceptors().get(baseURL + "/dealer/inspections");

// decision: "buy" | "decline"; reason is optional free text.
export const decideDealerInspection = (id, decision, reason) =>
  axiosInterceptors().post(baseURL + `/dealer/inspections/${encodeURIComponent(id)}/decision`, { decision, reason });

// What the customer wants the inspector to look at. Open while the inspection
// is Requested / Accepted / Scheduled (409 after); both answer with the updated
// inspection.
export const addDealerInspectionCheck = (id, text) =>
  axiosInterceptors().post(baseURL + `/dealer/inspections/${encodeURIComponent(id)}/checks`, { text });

export const removeDealerInspectionCheck = (id, checkId) =>
  axiosInterceptors().delete(baseURL + `/dealer/inspections/${encodeURIComponent(id)}/checks/${encodeURIComponent(checkId)}`);

export const getMyDealerInvoices = () =>
  axiosInterceptors().get(baseURL + "/dealer/invoices");

export const getMyDealerDeposit = () =>
  axiosInterceptors().get(baseURL + "/dealer/deposit");

// The PDF comes back as a Blob. The handler resolves error statuses too, so an
// error arrives as a JSON blob - check the type before showing it.
const fetchPdf = async (url) => {
  const blob = await axiosInterceptors().get(url, { responseType: "blob" });
  if (!blob || blob.type !== "application/pdf") throw new Error("The invoice could not be loaded");
  return blob;
};

export const getMyDealerInvoicePdf = (id) =>
  fetchPdf(baseURL + `/dealer/invoices/${encodeURIComponent(id)}/pdf`);

// Opens a PDF blob in a new tab. The tab is opened synchronously (before the
// download) so a popup blocker does not eat it, then pointed at the file.
export const openPdf = async (load) => {
  const tab = window.open("", "_blank");
  try {
    const blob = await load();
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (tab) tab.close();
    throw err;
  }
};

// ---- Staff ----

export const getDealerAdminSummary = () =>
  axiosInterceptors().get(baseURL + "/dealer/admin/summary");

// Customer accounts for the staff pickers: GET /users (user.controller), where
// `search` matches one first name, last name or email and `size` / `pageNo`
// page the list - it refuses anything else, `limit` included. An empty search
// lists the newest. { data: [...] }, or no body when nothing matches.
export const searchDealerAdminCustomers = (search, size = 20) =>
  axiosInterceptors().get(baseURL + "/users" + query({ search, size, pageNo: 1 }));

export const lookupDealerVehicle = (q) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/vehicle" + query({ q }));

export const getDealerAdminInquiries = (params) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/inquiries" + query(params));

export const updateDealerAdminInquiry = (id, data) =>
  axiosInterceptors().put(baseURL + `/dealer/admin/inquiries/${encodeURIComponent(id)}`, data);

// Opening a thread clears the staff-side unread counter server-side.
export const getDealerAdminInquiry = (id) =>
  axiosInterceptors().get(baseURL + `/dealer/admin/inquiries/${encodeURIComponent(id)}`);

export const replyToDealerAdminInquiry = (id, body, files) =>
  axiosInterceptors().post(baseURL + `/dealer/admin/inquiries/${encodeURIComponent(id)}/messages`, replyPayload(body, files));

// Moves one of a lead's extra cars to a lead of its own -> { inquiry, split }.
export const splitDealerAdminInquiryCar = (id, carId) =>
  axiosInterceptors().post(baseURL + `/dealer/admin/inquiries/${encodeURIComponent(id)}/cars/${encodeURIComponent(carId)}/split`, {});

// The same customer's other open leads, which this one can be moved into.
export const getDealerAdminMergeTargets = (id) =>
  axiosInterceptors().get(baseURL + `/dealer/admin/inquiries/${encodeURIComponent(id)}/merge-targets`);

// Moves a lead (cars, conversation, notes) into another of the customer's
// leads and closes it -> { inquiry, mergedId }.
export const mergeDealerAdminInquiry = (id, intoId) =>
  axiosInterceptors().post(baseURL + `/dealer/admin/inquiries/${encodeURIComponent(id)}/merge`, { intoId });

export const getDealerAdminFavorites = (params) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/favorites" + query(params));

// One customer's page. `ref` is { userId } | { inquiryId } | { inspectionId }
// (customerRefOf in Management/dealer/dealerAdmin.js) - never an email.
export const getDealerAdminCustomer = (ref) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/customer" + query(ref));

// The Customers list: accounts and guest leads matching every word; with no
// search, the customers on the most recently updated records.
export const searchDealerAdminCustomerRecords = (search, limit) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/customers" + query({ search, limit }));

export const addDealerAdminCustomerNote = (ref, body) =>
  axiosInterceptors().post(baseURL + "/dealer/admin/customer/notes", { ...ref, body });

export const removeDealerAdminCustomerNote = (id) =>
  axiosInterceptors().delete(baseURL + `/dealer/admin/customer/notes/${encodeURIComponent(id)}`);

export const getDealerAdminPurchases = (params) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/purchases" + query(params));

export const createDealerAdminPurchase = (data) =>
  axiosInterceptors().post(baseURL + "/dealer/admin/purchases", data);

// Includes statusNote / statusDate / notify for a step change: the note shows
// on the customer's timeline and in the email; notify:false skips the email.
export const updateDealerAdminPurchase = (id, data) =>
  axiosInterceptors().put(baseURL + `/dealer/admin/purchases/${encodeURIComponent(id)}`, data);

// With `nextStep`, `plan`, and `stepCopy` ({ [step]: { summary, next } }: what
// the customer reads when the car moves to each step); updates return the same.
export const getDealerAdminPurchase = (id) =>
  axiosInterceptors().get(baseURL + `/dealer/admin/purchases/${encodeURIComponent(id)}`);

export const getDealerAdminPurchaseTracking = (id) =>
  axiosInterceptors().get(baseURL + `/dealer/admin/purchases/${encodeURIComponent(id)}/tracking`);

// The ships and sailings other cars went on, latest departure first, to fill a
// car's shipping from. `exclude` leaves out the purchase being edited.
export const getDealerAdminSailings = (exclude) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/sailings" + query({ exclude }));

export const getDealerAdminInspections = (params) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/inspections" + query(params));

export const getDealerAdminInspection = (id) =>
  axiosInterceptors().get(baseURL + `/dealer/admin/inspections/${encodeURIComponent(id)}`);

// { inquiryId } or { userId, vehicleId }, plus status / scheduledFor / notify.
export const createDealerAdminInspection = (data) =>
  axiosInterceptors().post(baseURL + "/dealer/admin/inspections", data);

export const updateDealerAdminInspection = (id, data) =>
  axiosInterceptors().put(baseURL + `/dealer/admin/inspections/${encodeURIComponent(id)}`, data);

// They didn't buy: take the inspection fee from their deposit.
export const chargeDealerAdminInspectionFee = (id) =>
  axiosInterceptors().post(baseURL + `/dealer/admin/inspections/${encodeURIComponent(id)}/charge-fee`, {});

// files: File[] - photos and videos for the report.
export const uploadDealerInspectionMedia = (id, files) => {
  const form = new FormData();
  files.forEach((file) => form.append("inspectionImages", file));
  return axiosInterceptors().post(baseURL + `/dealer/admin/inspections/${encodeURIComponent(id)}/media`, form);
};

export const removeDealerInspectionMedia = (id, url) =>
  axiosInterceptors().delete(baseURL + `/dealer/admin/inspections/${encodeURIComponent(id)}/media`, { data: { url } });

// Paste an auction car's page link or id: { vehicleId, snapshot, existingPurchase }.
export const lookupAuctionCar = (q) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/auction-car" + query({ q }));

// Photos and video from our yard in Japan.
export const uploadDealerPurchaseMedia = (id, files) => {
  const form = new FormData();
  files.forEach((file) => form.append("inspectionImages", file));
  return axiosInterceptors().post(baseURL + `/dealer/admin/purchases/${encodeURIComponent(id)}/media`, form);
};

export const removeDealerPurchaseMedia = (id, url) =>
  axiosInterceptors().delete(baseURL + `/dealer/admin/purchases/${encodeURIComponent(id)}/media`, { data: { url } });

// "Deposit requested": { inspectionId | inquiryId, amount, externalRef?, payUrl?, notes? }.
// With a QuickBooks number it also records the invoice (opening a guest account
// for a customer without one).
export const requestDealerDeposit = (data) =>
  axiosInterceptors().post(baseURL + "/dealer/admin/deposit-requests", data);

export const clearDealerDepositRequest = (target) =>
  axiosInterceptors().delete(baseURL + "/dealer/admin/deposit-requests", { data: target });

// The deposit arrived: { inspectionId | inquiryId, amount, date (YYYY-MM-DD), method,
// reference? }. Into the customer's deposit balance, as in Billing (opening a guest
// account for a customer without one) -> { depositRequest, status, account }.
export const markDealerDepositReceived = (data) =>
  axiosInterceptors().post(baseURL + "/dealer/admin/deposit-requests/received", data);

// A guest account for the customer on a lead or inspection: { inspectionId | inquiryId,
// firstName?, lastName?, phone? } -> { account: { _id, isGuest, depositBalance }, created }.
export const openDealerGuestAccount = (data) =>
  axiosInterceptors().post(baseURL + "/dealer/admin/guest-accounts", data);

export const getDealerAdminInvoices = (params) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/invoices" + query(params));

// Pre-filled lines for a new invoice: { kind, purchaseId } | { kind, inspectionId } | { kind, userId }.
export const getDealerInvoiceDefaults = (params) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/invoices/defaults" + query(params));

export const createDealerAdminInvoice = (data) =>
  axiosInterceptors().post(baseURL + "/dealer/admin/invoices", data);

export const updateDealerAdminInvoice = (id, data) =>
  axiosInterceptors().put(baseURL + `/dealer/admin/invoices/${encodeURIComponent(id)}`, data);

export const deleteDealerAdminInvoice = (id) =>
  axiosInterceptors().delete(baseURL + `/dealer/admin/invoices/${encodeURIComponent(id)}`);

// Numbers, freezes and emails a draft; on a sent invoice, re-sends the email.
export const sendDealerAdminInvoice = (id, notify = true) =>
  axiosInterceptors().post(baseURL + `/dealer/admin/invoices/${encodeURIComponent(id)}/send`, { notify });

export const voidDealerAdminInvoice = (id, reason) =>
  axiosInterceptors().post(baseURL + `/dealer/admin/invoices/${encodeURIComponent(id)}/void`, { reason });

// Its copy in Zoho Invoice: send / retry now, or link to the one staff made there.
export const syncDealerAdminInvoiceToZoho = (id) =>
  axiosInterceptors().post(baseURL + `/dealer/admin/invoices/${encodeURIComponent(id)}/zoho`, {});

export const linkDealerAdminInvoiceToZoho = (id) =>
  axiosInterceptors().post(baseURL + `/dealer/admin/invoices/${encodeURIComponent(id)}/zoho/link`, {});

export const getDealerAdminInvoicePdf = (id) =>
  fetchPdf(baseURL + `/dealer/admin/invoices/${encodeURIComponent(id)}/pdf`);

export const getDealerAdminPayments = (params) =>
  axiosInterceptors().get(baseURL + "/dealer/admin/payments" + query(params));

export const recordDealerAdminPayment = (data) =>
  axiosInterceptors().post(baseURL + "/dealer/admin/payments", data);

export const voidDealerAdminPayment = (id) =>
  axiosInterceptors().post(baseURL + `/dealer/admin/payments/${encodeURIComponent(id)}/void`, {});

export const getDealerAdminDeposit = (userId) =>
  axiosInterceptors().get(baseURL + `/dealer/admin/deposits/${encodeURIComponent(userId)}`);

// { userId, type: deposit|refund|adjustment, amount, direction?, date, method, reference, notes }
export const recordDealerAdminDeposit = (data) =>
  axiosInterceptors().post(baseURL + "/dealer/admin/deposits", data);

// Mirrors PURCHASE_STEPS in Backend/app/utils/dealerPipeline.js (and the
// marketing site's copy). The API refuses anything not in this list, so the
// admin select must not offer more than the server accepts.
export const DEALER_PURCHASE_STATUSES = [
  "Awaiting Payment",
  "Order Confirmed",
  "Car Purchased",
  "Transport to Warehouse",
  "In Storage",
  "Export Processing",
  "Ship Booked",
  "In Transit",
  "Arrived at Port",
  "Customs Clearance",
  "Ready for Pickup",
  "Delivery Scheduled",
  "Completed",
  "Cancelled",
];

// Steps the admin board groups purchases under, in pipeline order.
export const PURCHASE_BOARD_COLUMNS = DEALER_PURCHASE_STATUSES.filter((s) => s !== "Cancelled");

export const DOCUMENT_CARRIERS = [
  { value: "ups", label: "UPS" },
  { value: "fedex", label: "FedEx" },
  { value: "usps", label: "USPS" },
  { value: "dhl", label: "DHL" },
  { value: "other", label: "Other" },
];

export const INSPECTION_STATUSES = ["Requested", "Accepted", "Scheduled", "Completed", "Cancelled"];

export const INQUIRY_STATUSES = ["new", "contacted", "searching", "closed"];

export const INQUIRY_STATUS_LABELS = {
  new: "New",
  contacted: "In conversation",
  searching: "Searching for another car",
  closed: "Closed",
};

export const INVOICE_KIND_LABELS = {
  vehicle: "Vehicle",
  inspection: "Inspection",
  deposit: "Deposit",
  other: "Other",
};

export const PAYMENT_METHOD_LABELS = {
  wire: "Wire transfer",
  ach: "ACH",
  quickbooks: "QuickBooks",
  deposit: "From deposit balance",
  check: "Check",
  cash: "Cash",
  card: "Card",
  other: "Other",
};

export const DEPOSIT_TYPE_LABELS = {
  opening: "Opening balance",
  deposit: "Deposit",
  refund: "Refund",
  applied: "Applied to invoice",
  adjustment: "Adjustment",
  inspection_fee: "Inspection fee",
};

export const formatUsd = (amount, { cents = true } = {}) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  }).format(n);
};

// "2026-10-13" (or an ISO timestamp) -> "Oct 13, 2026", without shifting a
// calendar date across a timezone.
export const formatDay = (value) => {
  if (!value) return "";
  const ymd = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = ymd ? new Date(Date.UTC(+ymd[1], +ymd[2] - 1, +ymd[3])) : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", ...(ymd ? { timeZone: "UTC" } : {}) });
};

export const INQUIRY_TYPE_LABELS = {
  inquiry: "Inquiry",
  quote: "Quote request",
  inspection: "Inspection request",
  contact: "Contact form",
};

/**
 * The cars on a lead, in one shape: its own car first, then the others the
 * customer asked about while it was open (`extraCars`, Backend
 * utils/leadCars.js). Each reads like a one-car lead - vehicleId, vehicle,
 * type, quote, message - so it can stand in for the lead wherever a single
 * car is meant (an inspection, a sale, a vehicle strip). `_id` is the extra
 * car's id, null for the lead's own.
 */
export const leadCars = (inquiry) => {
  if (!inquiry) return [];
  const own = inquiry.vehicleId
    ? [{
      _id: null,
      primary: true,
      vehicleId: inquiry.vehicleId,
      source: inquiry.source,
      vehicle: inquiry.vehicle,
      type: inquiry.type,
      quote: inquiry.quote,
      message: inquiry.message,
      addedAt: inquiry.createdAt,
    }]
    : [];
  return [...own, ...(inquiry.extraCars || []).map((car) => ({ ...car, primary: false }))];
};

/**
 * The fallback snapshot sent along with a favourite/inquiry, built from the
 * listing the page already has. Only the fields the API's schema accepts, and
 * only an http(s) image - a bundled placeholder path would be rejected.
 */
export const snapshotFromCar = (car) => {
  if (!car) return undefined;
  const image = Array.isArray(car.imageURLs) ? car.imageURLs[0] : "";
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const str = (v, max) => (v === null || v === undefined ? "" : String(v).slice(0, max));
  const snapshot = {
    source: car.source === "current-inventory" ? "current-inventory" : "dealer-stock",
    title: str(car.title || [car.year, car.make, car.model].filter(Boolean).join(" "), 200),
    year: num(car.year),
    make: str(car.make, 100),
    model: str(car.model, 100),
    grade: str(car.grade || car.description, 500),
    modelDetail: str(car.modelDetail, 200),
    mileage: num(car.mileage),
    transmission: str(car.transmission, 100),
    color: str(car.color, 100),
    bodyType: str(car.bodyType, 100),
    price: num(car.price),
    vehiclePrice: num(car.vehiclePrice),
    status: str(car.status, 40),
  };
  if (typeof image === "string" && /^https?:\/\//i.test(image)) {
    snapshot.imageURL = image.slice(0, 2048);
  }
  return snapshot;
};

/**
 * Turn a stored snapshot back into the listing shape DealerStockCard and the
 * price helpers expect (imageURLs array, `source`, raw price fields, _id).
 *
 * `source` 'auction' is a car we won at auction for the customer: `usdPrice`
 * is the winning price in USD and `_id` is the portal's /detail/:id car.
 */
export const carFromSnapshot = (record) => {
  const v = (record && record.vehicle) || {};
  return {
    _id: record.vehicleId,
    source: v.source || record.source || "dealer-stock",
    usdPrice: v.usdPrice,
    title: v.title,
    year: v.year,
    make: v.make,
    model: v.model,
    grade: v.grade,
    modelDetail: v.modelDetail,
    mileage: v.mileage,
    transmission: v.transmission,
    color: v.color,
    bodyType: v.bodyType,
    price: v.price,
    vehiclePrice: v.vehiclePrice,
    status: v.status,
    imageURLs: v.imageURL ? [v.imageURL] : [],
    url: v.url,
  };
};

/**
 * Records with each dealer-stock car's `usdPrice` set to today's price from the
 * main site (fetchDealerStockPrices), the way its own My Garage prices saved
 * cars. A snapshot's usdPrice is the price on the day it was saved, and older
 * snapshots have none; the portal does not convert their yen itself, since the
 * discount and the rate live only in the main site's server. A car the main
 * site no longer has keeps its snapshot price, and a failed request leaves
 * every record as it was.
 */
export const withTodaysPrices = async (records) => {
  const list = Array.isArray(records) ? records : [];
  // A lead's other cars (extraCars) are priced too.
  const ids = list
    .flatMap((r) => [r, ...(r.extraCars || [])])
    .filter((r) => r.vehicleId && carFromSnapshot(r).source === "dealer-stock")
    .map((r) => String(r.vehicleId));
  if (!ids.length) return list;
  const prices = await fetchDealerStockPrices(ids);
  const priced = (r) => {
    const today = r.vehicleId && prices[String(r.vehicleId)];
    return today ? { ...r, vehicle: { ...(r.vehicle || {}), usdPrice: today.usdPrice } } : r;
  };
  return list.map((r) => {
    const next = priced(r);
    return (r.extraCars || []).length ? { ...next, extraCars: r.extraCars.map(priced) } : next;
  });
};
